import { api } from '@goaltree/backend/convex/_generated/api'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useConvexAuth, useQuery } from 'convex/react'
import { Check, Sparkles as SparklesIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsPro } from '@/hooks/use-is-pro'
import { usePolar } from '@/hooks/use-polar'

export const Route = createFileRoute('/onboarding/paywall')({
  head: () => ({
    meta: [{ title: 'Unlock Pro — Goaltree' }],
  }),
  component: PaywallComponent,
})

function PaywallComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isPro, isLoading } = useIsPro()
  const { checkout, isConfigured } = usePolar()
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const { isAuthenticated } = useConvexAuth()
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip')
  const goals = useQuery(api.nodes.getUserGoals, isAuthenticated ? {} : 'skip')
  // Pricing-page signups arrive here before onboarding — continue into it.
  // Same condition as the _authenticated onboarding guard.
  const next =
    prefs &&
    goals &&
    !(prefs as { onboardingCompletedAt?: number }).onboardingCompletedAt &&
    !goals.some((g) => !g.isInbox)
      ? '/goals/new'
      : '/goals'

  const PRO_FEATURES = [
    { label: t('paywall.featureUnlimitedGoals'), free: false },
    { label: t('paywall.featureAI'), free: false },
    { label: t('paywall.featureVoice'), free: false },
    { label: t('paywall.featureCoachVoice'), free: false },
    { label: t('paywall.featureCoachMe'), free: false },
    { label: t('paywall.featureNative'), free: true },
    { label: t('paywall.featureStreak'), free: true },
    { label: t('paywall.featureSupport'), free: false },
  ]

  useEffect(() => {
    if (!isLoading && isPro) {
      navigate({ to: next })
    }
  }, [isPro, isLoading, navigate, next])

  async function handleUpgrade() {
    if (!isConfigured) {
      navigate({ to: next })
      return
    }
    setCheckoutLoading(true)
    const success = await checkout()
    setCheckoutLoading(false)
    if (success) navigate({ to: next })
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-lg px-4 py-12">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
          <SparklesIcon className="h-7 w-7 text-primary-text" />
        </div>
        <h1 className="text-3xl font-black tracking-tight">{t('paywall.title')}</h1>
        <p className="mt-2 text-muted-foreground">{t('paywall.subtitle')}</p>
      </div>

      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-primary-text">
            Pro
          </span>
          <div className="text-right">
            <span className="text-2xl font-black">$9</span>
            <span className="ml-1 text-sm text-muted-foreground">{t('paywall.perMonth')}</span>
          </div>
        </div>

        <ul className="mb-6 space-y-2.5">
          {PRO_FEATURES.map((f) => (
            <li key={f.label} className="flex items-center gap-3 text-sm">
              <Check className="h-4 w-4 shrink-0 text-primary-text" />
              <span className={f.free ? 'text-muted-foreground' : 'text-foreground'}>
                {f.label}
                {f.free && (
                  <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-xs">
                    {t('paywall.free')}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={handleUpgrade}
          disabled={checkoutLoading}
          className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-bold text-white dark:text-[#0a0a0a] transition-all hover:scale-[1.01] hover:bg-primary/90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100"
        >
          {checkoutLoading ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              {t('paywall.openingCheckout')}
            </>
          ) : (
            <>
              <SparklesIcon className="h-4 w-4" />
              {t('paywall.upgradeCta')}
            </>
          )}
        </button>
      </div>

      <button
        type="button"
        onClick={() => navigate({ to: next })}
        className="mt-4 w-full cursor-pointer py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        {t('paywall.continueFree')}
      </button>
    </div>
  )
}
